import { Injectable } from '@angular/core';

import { Observer, Subject, Subscription } from 'rxjs';

import { BillingInfoService } from '../models/billing-info/billing-info.service';
import { Tables } from '../models/cache/cache.model';
import { CostComparisonService } from '../models/cost-comparison/cost-comparison.service';
import { LocalStorage } from '../models/errors/error.model';
import { GroupService } from '../models/group/group.service';
import { InvoiceService } from '../models/invoice/invoice.service';
import { Organization } from '../models/organization/organization.model';
import { OrganizationService } from '../models/organization/organization.service';
import { PaymentPlanService } from '../models/payment-plan/payment-plan.service';
import { RegistrationFormService } from '../models/registration-form/registration-form.service';
import { RoleService } from '../models/role/role.service';
import { SubscriptionService } from '../models/subscription/subscription.service';
import { TeamService } from '../models/team/team.service';
import { User } from '../models/user/user.model';
import { UserService } from '../models/user/user.service';
import { ApiHelperService } from './api-helper.service';

@Injectable({
    providedIn: 'root'
})
export class StateService {

    private idCache  = {};
    private _loggedIn = false;
    get loggedIn(): boolean { return this._loggedIn; }

    private _user: Promise<User|undefined>; 
    private _organization: Promise<Organization|undefined>;

    private orgSubject: Subject<Organization> = new Subject<Organization>();
    private userSubject: Subject<User> = new Subject<User>();

    private localStorage: LocalStorage;

    constructor(
        private api: ApiHelperService,
        public billingInfoService: BillingInfoService,
        public costComparisonService: CostComparisonService,
        public groupService: GroupService,
        public teamService: TeamService,
        public invoiceService: InvoiceService,
        public paymentPlanService: PaymentPlanService,
        public registrationFormService: RegistrationFormService,
        public roleService: RoleService,
        public subscriptionService: SubscriptionService,
        public organizationService: OrganizationService,
        public userService: UserService
    ) {
        const ls = JSON.parse(localStorage.getItem('StaitmentLocalStorage') ?? '{}');
        this.localStorage = {
            firebaseUser: ls?.firebaseUser ?? null,
            appUser: ls?.appUser ?? null,
            appOrg: ls?.appOrg ?? null,
        };

        this._loggedIn = this.localStorage.appUser != null;

        // Don't fetch here — races ahead of /auth/bootstrap minting the JWT.
        this._user = Promise.resolve(undefined);
        this._organization = Promise.resolve(undefined);
    } 

    /****************************************************************
     *                            CACHING                           *
     ****************************************************************/

    public getById(table: Tables, id: string): Promise<unknown> {

        if (this.idCache[id] != null)
            return this.idCache[id];

        let ret: Promise<{ id: string }>;
        switch(table) {
            case Tables.BILLING_INFO:
                ret = this.billingInfoService.getBillingInfoById(id);
                break;
            case Tables.COST_COMPARISONS:
                ret = this.costComparisonService.getCostComparisionById(id);
                break;
            case Tables.GROUPS:
                ret = this.groupService.getGroupById(id);
                break;
            case Tables.INVOICES:
                ret = this.invoiceService.getInvoiceById(id);
                break;
            case Tables.ORGANIZATIONS:
                ret = this.organizationService.getOrganizationById(id) as Promise<{ id: string }>;
                break;
            case Tables.PAYMENT_PLANS:
                ret = this.paymentPlanService.getPlanById(id);
                break;
            case Tables.SUBSCRIPTIONS:
                ret = this.subscriptionService.getSubscriptionById(id);
                break;
            case Tables.TEAMS:
                ret = this.teamService.getTeamById(id);
                break;
            case Tables.USERS:
                ret = this.userService.getUserById(id) as Promise<{ id: string }>;
                break;
            default:
                throw Error(`${table} is not supported`);
        }

        return ret.then(x => {
            this.setById(x);
            return x as unknown;
        });
    }

    public setById(el: {id:string}) {
        this.idCache[el.id] = el;
    }

    /****************************************************************
     *                         SESSION STATE   
     ****************************************************************/

    get userId() { return this.localStorage.appUser; }

    get organizationId() { return this.localStorage.appOrg; }

    public getOrganization(): Promise<Organization|undefined> {
        if (!this.loggedIn)
            return this._organization;

        if (this.organizationId == null) {
            return Promise.resolve(undefined);
        }

        return this._organization.then(o => {
            if (o != null) {
                return o;
            }
            return this.getById(Tables.ORGANIZATIONS, this.organizationId!) as Promise<Organization>;
        });
    }

    public subscribeToOrganization(o: Observer<Organization>): Subscription {
        return this.orgSubject.subscribe(o);
    }

    public setOrganization(x: Organization|null) {
        this.localStorage.appOrg = x?.id ?? null;
        this._organization = Promise.resolve(x as Organization);
        this.orgSubject.next(x as Organization);
        this.syncLocalStorage();
    }

    /**
     * dbSync is kind of a hack that allows non-patch methods to emit
     */
    public updateOrganization(x: Partial<Organization>, dbSync = true) {
        if (x.id != null && x.id !== this.organizationId)
            throw Error('You must use the organization setter to change the organization');

        const orgPromise = dbSync
            ? this.organizationService.updateOrganization(this.organizationId!, x)
            : Promise.resolve(new Organization(x));

        this._organization = orgPromise.then((org: Organization) => {
            this.orgSubject.next(org);
            return org;
        });
        
    }

    public getUser(): Promise<User|undefined> {
        if (!this.loggedIn || this.userId == null)
            return this._user;

        return this._user.then(u => {
            if (u != null) {
                return u;
            }
            return this.getById(Tables.USERS, this.userId!) as Promise<User>;
        });
    }

    public subscribeToUser(u: Observer<User>): Subscription {
        return this.userSubject.subscribe(u);
    }

    public setUser(x:User) {
        this.localStorage.appUser = x.id;
        this._user = Promise.resolve(x);
        this.userSubject.next(x);
        this.syncLocalStorage();
        this._loggedIn = true;
    } 

    get firebaseUser():  User {
        return this.localStorage.firebaseUser as User;
    }

    set firebaseUser(x: User){
        this.localStorage.firebaseUser = x;
        this.syncLocalStorage();
    } 

    public updateUser(x: Partial<User>, dbSync = true): void {
        if (x.id != null && x.id !== this.userId)
            throw Error('You must use the user setter to change the user');

        const userPromise = dbSync
            ? this.userService.patchUser(this.userId!, x)
            : Promise.resolve(new User(x));

        this._user = userPromise
            .then((user: User) => {
                this.userSubject.next(user);
                return user;
            });
    }

    public purge() {
        this.purgeLocalStorage();
        this._loggedIn = false;
    }

    private syncLocalStorage() {
        localStorage.setItem('StaitmentLocalStorage', JSON.stringify(this.localStorage));
    }

    private purgeLocalStorage() {
        this.localStorage = {
            firebaseUser: null,
            appUser: null,
            appOrg: null
        };
        localStorage.removeItem('StaitmentLocalStorage');
    }
}
